import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useCompany } from '../context/CompanyContext';
import {
  Plus, Pencil, Trash2, MoreHorizontal, Loader2, Landmark, DollarSign, CreditCard, Wallet, PiggyBank
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const fmtCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(v || 0);

const ACCOUNT_TYPES = [
  { value: 'checking', label: 'Checking', icon: Landmark },
  { value: 'savings', label: 'Savings', icon: PiggyBank },
  { value: 'credit', label: 'Credit Card', icon: CreditCard },
  { value: 'investment', label: 'Investment / Brokerage', icon: DollarSign },
  { value: 'cash', label: 'Cash', icon: Wallet },
  { value: 'other', label: 'Other', icon: Landmark },
];

const ACCOUNT_COLORS = {
  checking: '#3B82F6', savings: '#10B981', credit: '#EF4444',
  investment: '#8B5CF6', cash: '#F59E0B', other: '#6B7280'
};

function AccountDialog({ open, onOpenChange, account, companyId, onSave }) {
  const [form, setForm] = useState({
    account_name: '', institution: '', account_type: 'checking',
    account_number: '', routing_number: '', balance: '',
    credit_limit: '', interest_rate: '', notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      if (account) {
        const meta = parseMeta(account.notes);
        setForm({
          account_name: account.name || '',
          institution: account.institution || '',
          account_type: meta.account_type || 'checking',
          account_number: account.account_number || '',
          routing_number: meta.routing_number || '',
          balance: account.value != null ? String(account.value) : '',
          credit_limit: meta.credit_limit != null ? String(meta.credit_limit) : '',
          interest_rate: meta.interest_rate != null ? String(meta.interest_rate) : '',
          notes: meta.notes_text || ''
        });
      } else {
        setForm({ account_name: '', institution: '', account_type: 'checking', account_number: '', routing_number: '', balance: '', credit_limit: '', interest_rate: '', notes: '' });
      }
    }
  }, [account, open]);

  const handleSubmit = async () => {
    if (!form.account_name.trim()) { toast.error('Account name is required'); return; }
    if (!form.institution.trim()) { toast.error('Institution is required'); return; }
    setSubmitting(true);
    try {
      const payload = {
        company_id: companyId,
        name: form.account_name,
        asset_type: form.account_type === 'cash' ? 'cash' : 'bank_account',
        value: parseFloat(form.balance) || 0,
        institution: form.institution,
        account_number: form.account_number || null,
        notes: JSON.stringify({
          account_type: form.account_type,
          routing_number: form.routing_number,
          credit_limit: form.credit_limit ? parseFloat(form.credit_limit) : null,
          interest_rate: form.interest_rate ? parseFloat(form.interest_rate) : null,
          notes_text: form.notes
        }),
        tags: [form.account_type]
      };
      if (account) {
        await axios.put(`${API}/api/assets/${account.id}`, payload, { withCredentials: true });
        toast.success('Account updated');
      } else {
        await axios.post(`${API}/api/assets`, payload, { withCredentials: true });
        toast.success('Account added');
      }
      onSave(); onOpenChange(false);
    } catch { toast.error('Failed to save'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{account ? 'Edit Account' : 'Add Financial Account'}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Account Name *</Label><Input value={form.account_name} onChange={e => setForm(p => ({...p, account_name: e.target.value}))} placeholder="e.g., Primary Checking" data-testid="account-name" /></div>
            <div><Label>Institution *</Label><Input value={form.institution} onChange={e => setForm(p => ({...p, institution: e.target.value}))} placeholder="e.g., Chase, USAA" data-testid="account-institution" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Account Type</Label>
              <Select value={form.account_type} onValueChange={v => setForm(p => ({...p, account_type: v}))}>
                <SelectTrigger data-testid="account-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ACCOUNT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Current Balance *</Label><Input type="number" step="0.01" value={form.balance} onChange={e => setForm(p => ({...p, balance: e.target.value}))} placeholder="0.00" data-testid="account-balance" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Account Number (last 4)</Label><Input value={form.account_number} onChange={e => setForm(p => ({...p, account_number: e.target.value}))} placeholder="****1234" data-testid="account-number" /></div>
            <div><Label>Routing Number</Label><Input value={form.routing_number} onChange={e => setForm(p => ({...p, routing_number: e.target.value}))} placeholder="Optional" data-testid="account-routing" /></div>
          </div>
          {(form.account_type === 'credit' || form.account_type === 'investment') && (
            <div className="grid grid-cols-2 gap-3">
              {form.account_type === 'credit' && <div><Label>Credit Limit</Label><Input type="number" value={form.credit_limit} onChange={e => setForm(p => ({...p, credit_limit: e.target.value}))} placeholder="0.00" /></div>}
              <div><Label>Interest Rate (%)</Label><Input type="number" step="0.01" value={form.interest_rate} onChange={e => setForm(p => ({...p, interest_rate: e.target.value}))} placeholder="0.00" /></div>
            </div>
          )}
          <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} placeholder="Optional notes" data-testid="account-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="save-account-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : account ? 'Update' : 'Add Account'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function parseMeta(notesStr) {
  try { return JSON.parse(notesStr); } catch { return {}; }
}

export default function FinancialAccountsPage() {
  const { currentCompany } = useCompany();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editAccount, setEditAccount] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');

  const fetchAccounts = useCallback(async () => {
    if (!currentCompany) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/assets`, { params: { company_id: currentCompany.id }, withCredentials: true });
      // Filter to bank accounts and cash only
      const financialAssets = res.data.filter(a => a.asset_type === 'bank_account' || a.asset_type === 'cash');
      setAccounts(financialAssets);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [currentCompany]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/api/assets/${deleteTarget.id}`, { withCredentials: true });
      toast.success('Account deleted'); setDeleteTarget(null); fetchAccounts();
    } catch { toast.error('Failed'); }
  };

  const filtered = typeFilter === 'all' ? accounts : accounts.filter(a => {
    const meta = parseMeta(a.notes);
    return meta.account_type === typeFilter;
  });

  const totalBalance = accounts.reduce((s, a) => s + (a.value || 0), 0);
  const uniqueTypes = [...new Set(accounts.map(a => { const m = parseMeta(a.notes); return m.account_type || 'checking'; }))];

  if (!currentCompany) return null;
  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-gray-500" /></div>;

  return (
    <div className="space-y-6" data-testid="financial-accounts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Financial Accounts</h1>
          <p className="text-gray-500 text-sm mt-0.5">Track all your bank accounts, cards, and cash</p>
        </div>
        <Button onClick={() => setAddOpen(true)} data-testid="add-account-btn">
          <Plus className="w-4 h-4 mr-2" />Add Account
        </Button>
      </div>

      {/* Total Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6">
        <p className="text-blue-100 text-sm">Total Balance</p>
        <p className="text-4xl font-bold text-white mt-1" data-testid="total-balance">{fmtCurrency(totalBalance)}</p>
        <p className="text-blue-200 text-sm mt-1">{accounts.length} account{accounts.length !== 1 ? 's' : ''}</p>
      </div>

      {/* Type Filter */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setTypeFilter('all')} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${typeFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          All ({accounts.length})
        </button>
        {uniqueTypes.map(t => {
          const info = ACCOUNT_TYPES.find(at => at.value === t);
          const count = accounts.filter(a => { const m = parseMeta(a.notes); return (m.account_type || 'checking') === t; }).length;
          return (
            <button key={t} onClick={() => setTypeFilter(typeFilter === t ? 'all' : t)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${typeFilter === t ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              style={typeFilter === t ? { backgroundColor: ACCOUNT_COLORS[t] } : {}}>
              {info?.label || t} ({count})
            </button>
          );
        })}
      </div>

      {/* Account Cards */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Landmark className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No accounts yet</h3>
          <p className="text-gray-500 mb-4">Add your first financial account to start tracking</p>
          <Button onClick={() => setAddOpen(true)}><Plus className="w-4 h-4 mr-2" />Add Account</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(account => {
            const meta = parseMeta(account.notes);
            const acctType = meta.account_type || 'checking';
            const typeInfo = ACCOUNT_TYPES.find(t => t.value === acctType);
            const Icon = typeInfo?.icon || Landmark;
            const color = ACCOUNT_COLORS[acctType] || '#6B7280';
            return (
              <div key={account.id} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow" data-testid={`account-card-${account.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
                      <Icon className="w-5 h-5" style={{ color }} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{account.name}</h3>
                      <p className="text-sm text-gray-500">{account.institution}</p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="h-8 w-8 p-0"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setEditAccount(account)}><Pencil className="w-3.5 h-3.5 mr-2" />Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDeleteTarget(account)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="flex items-center justify-between">
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: `${color}15`, color }}>{typeInfo?.label}</span>
                  {account.account_number && <span className="text-xs text-gray-400 font-mono">****{account.account_number.replace(/[^0-9]/g, '').slice(-4)}</span>}
                </div>
                <p className="text-2xl font-bold text-gray-900 mt-3">{fmtCurrency(account.value)}</p>
                {meta.credit_limit && <p className="text-xs text-gray-500 mt-1">Limit: {fmtCurrency(meta.credit_limit)}</p>}
                {meta.interest_rate && <p className="text-xs text-gray-500">APR: {meta.interest_rate}%</p>}
              </div>
            );
          })}
        </div>
      )}

      {/* Dialogs */}
      <AccountDialog open={addOpen || !!editAccount} onOpenChange={v => { if (!v) { setAddOpen(false); setEditAccount(null); } }}
        account={editAccount} companyId={currentCompany?.id} onSave={fetchAccounts} />

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Account</AlertDialogTitle>
            <AlertDialogDescription>Delete "{deleteTarget?.name}" at {deleteTarget?.institution}? This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
